import React from 'react';
import {DayPickerRangeController} from 'react-dates';
import moment, {Moment} from 'moment';
import onClickOutside from 'react-onclickoutside';
import {px} from 'csx';
import {I18n} from '../i18n/i18n';
import {LightGreyColor, GreyColor} from '../styles/colors';
import {stylesheet} from 'typestyle';

interface DateCompareRangeSelectorProps {
    alignmentSide: string;
    onDatesChange: (startDate: Moment | null, endDate: Moment | null) => void;
    startDate: Moment | null;
    countSelectableDays: number;
    width?: number;
}

interface DateCompareRangeSelectorState {
    focused: 'startDate' | 'endDate' | null;
    datepickerVisible: boolean;
}

class DateCompareRangeSelector extends React.Component<DateCompareRangeSelectorProps, DateCompareRangeSelectorState> {
    public readonly state: DateCompareRangeSelectorState = {
        focused: null,
        datepickerVisible: false,
    };

    public handleClickOutside = (): void => {
        this.changeDatepickerVisibility(false);
    };

    private getFocusedInput(): 'startDate' | 'endDate' {
        return this.state.focused || 'startDate';
    }

    private calculateEndDate = (): Moment | null => {
        if (this.props.startDate !== null) {
            return moment(this.props.startDate).add(this.props.countSelectableDays, 'days');
        }
        return null;
    };

    private getTickPosition(): number {
        return this.props.alignmentSide === 'left' ? 132 : 465;
    }

    private getInitialVisibleMonth = (): Moment => {
        return this.props.startDate || moment();
    };

    private insertDatepicker(): React.ReactNode | null {
        if (this.state.datepickerVisible) {
            return (
                <div>
                    <svg
                        role="presentation"
                        focusable="false"
                        className="DateInput_fang DateInput_fang_1"
                        style={{top: 15, left: this.getTickPosition()}}>
                        <path className="DateInput_fangShape DateInput_fangShape_1" d="M0,10 20,10 10,0z" />
                        <path className="DateInput_fangStroke DateInput_fangStroke_1" d="M0,10 10,0 20,10" />
                    </svg>
                    <DayPickerRangeController
                        onDatesChange={({startDate, endDate}) => this.props.onDatesChange(startDate, endDate)}
                        onFocusChange={(focusedInput) => this.setState({focused: focusedInput})}
                        focusedInput={this.getFocusedInput()}
                        initialVisibleMonth={this.getInitialVisibleMonth}
                        startDate={this.props.startDate}
                        endDateOffset={(startDate) => startDate.add(this.props.countSelectableDays, 'days')}
                        numberOfMonths={2}
                        hideKeyboardShortcutsPanel={true}
                        isOutsideRange={() => false}
                        endDate={this.calculateEndDate()}
                    />
                </div>
            );
        }
        return null;
    }

    private openDatepicker = (e: React.FormEvent<EventTarget>): void => {
        e.preventDefault();
        e.stopPropagation();
        this.changeDatepickerVisibility(true);
    };

    private changeDatepickerVisibility(isVisible: boolean): void {
        this.setState({
            datepickerVisible: isVisible,
        });
    }

    private preventClicks = (e: React.FormEvent<EventTarget>): void => {
        e.preventDefault();
        e.stopPropagation();
    };

    private showSelectedDates(): React.ReactNode {
        if (this.props.startDate) {
            const endDate = this.calculateEndDate();
            return (
                <React.Fragment>
                    <div className={compareCss.dateBoxContent}>{this.props.startDate.format('L')}</div>
                    <div style={{width: 24}}>
                        <svg className="DateRangePickerInput_arrow_svg DateRangePickerInput_arrow_svg_1" viewBox="0 0 1000 1000">
                            <path d="M694.4 242.4l249.1 249.1c11 11 11 21 0 32L694.4 772.7c-5 5-10 7-16 7s-11-2-16-7c-11-11-11-21 0-32l210.1-210.1H67.1c-13 0-23-10-23-23s10-23 23-23h805.4L662.4 274.5c-21-21.1 11-53.1 32-32.1z" />
                        </svg>
                    </div>
                    <div className={compareCss.dateBoxContent}>{endDate ? endDate.format('L') : null}</div>
                </React.Fragment>
            );
        }
        return (
            <div
                style={{
                    textAlign: 'center',
                    color: GreyColor,
                    lineHeight: px(31),
                }}>
                <I18n i18nKey="date.comparison" />
            </div>
        );
    }

    private generateFoldoutStyle(): React.CSSProperties {
        return {
            position: 'absolute',
            top: 45,
            [this.props.alignmentSide]: 0,
        };
    }

    public render(): React.ReactNode {
        const {width} = this.props;
        const foldoutStyle = this.generateFoldoutStyle();
        return (
            <div className={compareCss.container} style={{width: width || 320}} onClick={this.preventClicks}>
                <div className={compareCss.dateBox} onClick={this.openDatepicker}>
                    {this.showSelectedDates()}
                </div>
                <div style={foldoutStyle}>{this.insertDatepicker()}</div>
            </div>
        );
    }
}

export default onClickOutside(DateCompareRangeSelector);

const compareCss = stylesheet({
    dateBoxContent: {
        flex: 1,
        textAlign: 'center',
    },
    dateBox: {
        height: 34,
        border: '1px solid ' + LightGreyColor,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
    },
    container: {
        position: 'relative',
        zIndex: 1,
        pointerEvents: 'all',
    },
});



// WEBPACK FOOTER //
// ./src/views/DateCompareRangeSelector.tsx